import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ORG_LOGOS, INSTITUTION_IMG } from "@/lib/api";
import { ShieldCheck, Users2, Globe2, Scale } from "lucide-react";

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="font-heading text-lg font-semibold text-primary mb-2">{title}</h3>
    <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
  </div>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">About the boards</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-primary tracking-tight">
            Independent. Specialized. Publicly accountable.
          </h1>
          <p className="mt-5 max-w-3xl text-slate-600 leading-relaxed">
            ACIS and IBTHM share a unified credentialing framework while maintaining specialty
            independence in their respective domains. Both operate under a published code of standards
            and a transparent disciplinary process.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <Tabs defaultValue="acis" className="w-full">
              <TabsList className="bg-slate-100 p-1">
                <TabsTrigger value="acis" data-testid="tab-acis">ACIS</TabsTrigger>
                <TabsTrigger value="ibthm" data-testid="tab-ibthm">IBTHM</TabsTrigger>
                <TabsTrigger value="governance" data-testid="tab-governance">Governance</TabsTrigger>
              </TabsList>

              <TabsContent value="acis" className="mt-6">
                <div className="flex items-center gap-4 mb-6">
                  <img src={ORG_LOGOS.ACIS} alt="ACIS" className="h-14 w-14 object-contain" />
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-primary">Accreditation Commission for Integrative Sciences</h2>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mt-1">Est. — Public Interest Charter</div>
                  </div>
                </div>
                <Section title="Mandate">
                  ACIS accredits institutions and educational programs across integrative health
                  sciences. The Commission evaluates institutional governance, faculty qualifications,
                  curriculum quality, learning outcomes, and operational sustainability prior to
                  granting institutional or programmatic accreditation. ACIS does not credential,
                  certify, or license individual practitioners.
                </Section>
                <Section title="Accreditation scopes">
                  Institutional Accreditation (applies to the entire institution) and Programmatic
                  Accreditation (applies to specific approved programs). Practitioner credentialing
                  and licensure are administered separately by IBTHM.
                </Section>
                <Section title="Public reporting">
                  ACIS publishes accreditation status, expiration dates, and any disciplinary actions
                  on approved institutions in the public registry. Verification is free and does not
                  require an account.
                </Section>
              </TabsContent>

              <TabsContent value="ibthm" className="mt-6">
                <div className="flex items-center gap-4 mb-6">
                  <img src={ORG_LOGOS.IBTHM} alt="IBTHM" className="h-14 w-14 object-contain" />
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-primary">International Board of Trichology &amp; Holistic Medicine</h2>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mt-1">Specialty board · International</div>
                  </div>
                </div>
                <Section title="Mandate">
                  IBTHM evaluates practitioners across integrative health sciences for board
                  certification and professional licensure. The Board reviews academic preparation,
                  supervised clinical experience, competency examinations, continuing education,
                  ethical conduct, and professional standing before issuing credentials. IBTHM does
                  not accredit institutions or approve curricula — those functions belong to ACIS.
                </Section>
                <Section title="Recognition pathway">
                  Applicants demonstrate didactic preparation, documented clinical hours under
                  supervision, case-log review, and continuing-education compliance.
                </Section>
                <Section title="International reach">
                  IBTHM credentials are recognized by partner institutions across multiple
                  jurisdictions. Reciprocity is governed by published agreements.
                </Section>
              </TabsContent>

              <TabsContent value="governance" className="mt-6">
                <h2 className="font-heading text-2xl font-bold text-primary mb-4">Governance &amp; ethics</h2>
                <Section title="Independent review committees">
                  Each application is assigned to a credentialing committee independent of the
                  applicant's training institution and referrers.
                </Section>
                <Section title="Conflict of interest">
                  Reviewers must declare any prior relationship with the applicant. Conflicts trigger
                  reassignment, recorded in the audit log.
                </Section>
                <Section title="Disciplinary process">
                  Complaints are screened, investigated, and (where warranted) adjudicated. Outcomes
                  are published in the public registry and the practitioner's credential record.
                </Section>
                <Section title="Audit logs">
                  All credentialing actions — submissions, reviews, issuances, suspensions,
                  reinstatements — are recorded in an immutable audit log retained per policy.
                </Section>
              </TabsContent>
            </Tabs>
          </div>

          <div className="lg:col-span-4 space-y-4">
            <img src={INSTITUTION_IMG} alt="" className="rounded-lg border border-slate-200 w-full h-56 object-cover" />
            {[
              { icon: ShieldCheck, label: "Public-interest charter" },
              { icon: Users2, label: "Independent review committees" },
              { icon: Scale, label: "Transparent disciplinary process" },
              { icon: Globe2, label: "International reciprocity (IBTHM)" },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-4">
                <b.icon className="h-5 w-5 text-primary" />
                <div className="text-sm font-medium text-slate-700">{b.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
